import * as THREE from 'three';
// Create a simple markRaw function that returns the object unchanged
// This allows the code to work even if Vue is not available
const markRaw = (obj) => obj;

import IslandGenerator from '../island/IslandGenerator.js';
import BiomeManager from '../island/BiomeManager.js';
import TextureManager from '../island/TextureManager.js';
import VegetationSystem from '../island/VegetationSystem.js';
import PointsOfInterestSystem from '../island/PointsOfInterestSystem.js';
import OptimizationSystem from '../island/OptimizationSystem.js';
import PerformanceBenchmark from '../island/PerformanceBenchmark.js';

// Constants for player positioning
const TERRAIN_Y_OFFSET = 0.05; // Height offset from terrain (increased to ensure players are clearly above ground)

/**
 * IslandMapScene - Responsible for creating and managing the procedurally generated island map
 * Uses IslandGenerator to create island shape and terrain height
 */
class IslandMapScene {
  /**
   * Constructor for the island map scene
   * @param {Object} options - Options for map generation
   * @param {number} options.size - Size of the island map (default: 256)
   * @param {string|number} options.seed - Seed for procedural generation (default: random)
   * @param {boolean} options.debug - Whether to show debug visualizations (default: false)
   * @param {number} options.spawnPointCount - Number of spawn points to generate (default: 8)
   * @param {boolean} options.enableVegetation - Whether to enable vegetation (default: true)
   * @param {number} options.treeDensity - Tree density factor (default: 0.3)
   * @param {number} options.bushDensity - Bush density factor (default: 0.5)
   * @param {number} options.grassDensity - Grass density factor (default: 0.7)
   * @param {boolean} options.enableLandmarks - Whether to enable landmarks (default: true)
   * @param {number} options.landmarkCount - Number of landmarks to place (default: 5)
   * @param {boolean} options.enableOptimization - Whether to enable optimization techniques (default: true)
   * @param {boolean} options.benchmark - Whether to benchmark performance (default: false)
   * @param {Object} options.gpuPerformance - GPU performance options
   * @param {boolean} options.gpuPerformance.enableShadows - Whether to enable shadows (default: true)
   * @param {number} options.gpuPerformance.shadowMapSize - Shadow map size (default: 1024)
   * @param {string} options.gpuPerformance.textureQuality - Texture quality (default: medium)
   * @param {boolean} options.gpuPerformance.useCompressedTextures - Whether to use compressed textures (default: true)
   * @param {number} options.gpuPerformance.maxInstancedObjects - Maximum number of instanced objects (default: 1000)
   * @param {boolean} options.gpuPerformance.enableFog - Whether to enable fog (default: true)
   * @param {number} options.gpuPerformance.drawDistance - Draw distance (default: 250)
   * @param {boolean} options.gpuPerformance.useShaderLOD - Whether to use shader LOD (default: true)
   * @param {boolean} options.gpuPerformance.enablePostprocessing - Whether to enable postprocessing (default: false)
   */
  constructor(options = {}) {
    // Set default options
    this.options = {
      size: 512,
      seed: 'unified-terrain',//Math.random().toString(36).substring(2, 15),
      debug: false,
      spawnPointCount: 8,
      enableVegetation: true,
      treeDensity: 0.3,
      bushDensity: 0.5,
      grassDensity: 0.7,
      enableLandmarks: true,
      landmarkCount: 5,
      enableOptimization: true,
      benchmark: false,
      // Add GPU performance options
      gpuPerformance: {
        enableShadows: true,
        shadowMapSize: 1024,
        textureQuality: 'medium', // low, medium, high
        useCompressedTextures: true,
        maxInstancedObjects: 1000,
        enableFog: true,
        drawDistance: 250,
        useShaderLOD: true,
        enablePostprocessing: false
      },
      ...options
    };
    
    // Initialize collection for map objects
    this.objects = [];
    
    // Initialize collection for collision objects
    this.collisionObjects = [];
    
    // Store heightmap data for later use
    this.heightMap = null;
    this.islandMask = null;
    
    // Initialize array for spawn points
    this.spawnPoints = [];
    
    // Debug visualizations
    this.debugObjects = [];
    
    // Vegetation system
    this.vegetationSystem = null;
    
    // Points of Interest system
    this.poiSystem = null;
    
    // Optimization system
    this.optimizationSystem = null;
    
    // Performance benchmark
    this.benchmark = null;
    
    // Reference to the scene for optimization updates
    this.scene = null; // Store the scene reference

    // Reference to the current terrain LOD object
    this.terrainLOD = null;
    
    // Flag to track if the scene has been created
    this.isCreated = false;
    
    // Performance tracking
    this.lastUpdateTime = 0;
    
    // Initialize performance benchmark if enabled
    if (this.options.benchmark) {
      this.benchmark = new PerformanceBenchmark();
    }
    
    // Make this object non-reactive
    return markRaw(this);
  }

  /**
   * Create the island map and add it to the provided scene
   * @param {THREE.Scene} scene - The Three.js scene to add map objects to
   */
  async createMap(scene) {
    if (!scene) {
      console.error('Scene is required to create map');
      return;
    }

    // Store reference to scene
    this.scene = scene;

    // Set performance level to auto if not specified explicitly
    if (this.options.gpuPerformance && !this.options.gpuPerformance.textureQuality) {
      this.setGPUPerformanceLevel('auto');
    }

    console.log(`Generating island map with seed: ${this.options.seed}`);
    
    // Start generation benchmark if enabled
    if (this.benchmark) {
      this.benchmark.startGenerationMeasurement();
    }
    
    // Generate the island mask
    this.islandMask = IslandGenerator.generateIslandMask(
      this.options.size, 
      this.options.size, 
      { seed: this.options.seed }
    );
    
    // Record island mask generation time
    if (this.benchmark) {
      this.benchmark.measureGenerationPhase('islandMask');
    }
    
    // Generate the height map based on the island mask
    this.heightMap = IslandGenerator.generateHeightMap(
      this.options.size, 
      this.options.size, 
      this.islandMask, 
      { seed: this.options.seed }
    );
    
    // Record height map generation time
    if (this.benchmark) {
      this.benchmark.measureGenerationPhase('heightMap');
    }
    
    // Generate a moisture map for biome assignment
    const moistureMap = BiomeManager.generateMoistureMap(
      this.options.size,
      this.options.size,
      { seed: this.options.seed + '-moisture' }
    );
    
    // Generate biome map based on height and moisture
    this.biomeMap = BiomeManager.generateBiomeMap(
      this.heightMap,
      moistureMap,
      this.islandMask
    );
    
    // Record biome map generation time
    if (this.benchmark) {
      this.benchmark.measureGenerationPhase('biomeMap');
    }
    
    // Create the terrain mesh
    const terrainMesh = this.createTerrainMesh();
    
    // Apply shader optimizations to the terrain mesh
    const optimizedTerrainMesh = this.optimizeMesh(terrainMesh);
    
    // Record terrain mesh generation time
    if (this.benchmark) {
      this.benchmark.measureGenerationPhase('terrainMesh');
    }
    
    // Initialize optimization system if enabled
    if (this.options.enableOptimization) {
      this.initializeOptimizationSystem();

      // Setup quality change listener AFTER optimization system is initialized
      this.setupQualityChangeListener();

      // Apply LOD to terrain
      // Store the LOD object reference
      this.terrainLOD = this.optimizationSystem.applyTerrainLOD(optimizedTerrainMesh, scene);
      this.objects.push(this.terrainLOD);
      this.collisionObjects.push(this.terrainLOD); // Add LOD for potential broad phase collision/raycasting
      console.log('Terrain LOD applied.');
      
      // Record optimization setup time
      if (this.benchmark) {
        this.benchmark.measureGenerationPhase('optimization');
      }
    } else {
      // Add terrain directly to scene without LOD
      scene.add(optimizedTerrainMesh);
      this.objects.push(optimizedTerrainMesh);
      this.collisionObjects.push(optimizedTerrainMesh);
      console.log('Base terrain mesh added.');
    }
    
    // Create a debug visualization if debug mode is enabled
    if (this.options.debug) {
      this.createDebugVisualizations(scene);
    }
    
    // Place spawn points around the island
    this.placeSpawnPoints();
    
    // Create vegetation if enabled
    if (this.options.enableVegetation) {
      this.createVegetation(scene);
      
      // Record vegetation generation time
      if (this.benchmark) {
        this.benchmark.measureGenerationPhase('vegetation');
      }
    }
    
    // Create landmarks (points of interest) if enabled
    if (this.options.enableLandmarks) {
      this.createLandmarks(scene);
      
      // Record landmarks generation time
      if (this.benchmark) {
        this.benchmark.measureGenerationPhase('landmarks');
      }
    }
    
    // Apply performance settings to shadows if scene has a directional light
    if (scene) {
      scene.traverse(object => {
        if (object.isDirectionalLight && object.shadow) {
          const shadowSettings = this.options.gpuPerformance || {};
          const enableShadows = shadowSettings.enableShadows !== undefined ? shadowSettings.enableShadows : true;
          const shadowMapSize = shadowSettings.shadowMapSize || 1024;
          
          // Update shadow settings based on performance options
          object.castShadow = enableShadows;
          if (enableShadows) {
            object.shadow.mapSize.width = shadowMapSize;
            object.shadow.mapSize.height = shadowMapSize;
            
            // Adjust shadow camera based on shadow map size for performance
            const shadowRange = shadowMapSize >= 2048 ? 50 : 
                              shadowMapSize >= 1024 ? 40 : 30;
            object.shadow.camera.left = -shadowRange;
            object.shadow.camera.right = shadowRange;
            object.shadow.camera.top = shadowRange;
            object.shadow.camera.bottom = -shadowRange;
            object.shadow.camera.updateProjectionMatrix();
            
            // Other shadow optimizations
            object.shadow.bias = -0.0005;
            object.shadow.normalBias = 0.02;
            
            // Use percentage closer filtering for high quality shadows, basic for low
            const quality = shadowSettings.textureQuality || 'medium';
            object.shadow.radius = quality === 'low' ? 1 : quality === 'medium' ? 2 : 4;
          }
        }
      });
    }
    
    // End generation benchmark if enabled
    if (this.benchmark) {
      const benchmarkResults = this.benchmark.endGenerationMeasurement();
      console.log('Island generation benchmark results:', benchmarkResults);
      
      // Dispatch benchmark results event if in browser
      if (typeof window !== 'undefined') {
        const event = new CustomEvent('island-generation-benchmarked', {
          detail: benchmarkResults
        });
        window.dispatchEvent(event);
      }
    }
    
    // Set created flag
    this.isCreated = true;
    
    console.log('Island map created successfully');
    return this.objects;
  }

  /**
   * Initialize the optimization system
   */
  initializeOptimizationSystem() {
    // Get GPU performance settings with fallbacks
    const gpuPerf = this.options.gpuPerformance || {};
    const drawDistance = gpuPerf.drawDistance || 250;
    
    // Calculate LOD distances based on draw distance
    const lodNear = drawDistance * 0.15; // Close distance for highest detail
    const lodFar = drawDistance * 0.8;   // Far distance for lowest detail
    
    // LOD levels based on quality
    let lodLevels = 3; // Default
    if (gpuPerf.textureQuality === 'low') {
      lodLevels = 2;   // Fewer levels for low quality
    } else if (gpuPerf.textureQuality === 'high') {
      lodLevels = 4;   // More levels for high quality
    }
    
    // Create optimization system with settings based on GPU performance
    this.optimizationSystem = new OptimizationSystem({
      enableLOD: true,
      enableFrustumCulling: true,
      enablePerformanceAdaptation: true,
      enableFog: gpuPerf.enableFog !== undefined ? gpuPerf.enableFog : true,
      fogNear: lodNear * 0.8,
      fogFar: drawDistance * 0.9,
      lodLevels: lodLevels,
      lodNear: lodNear,
      lodFar: lodFar,
      maxInstancedObjects: gpuPerf.maxInstancedObjects || 1000,
      useShaderLOD: gpuPerf.useShaderLOD !== undefined ? gpuPerf.useShaderLOD : true,
      instanceCullingThreshold: lodFar * 1.2, // Cull instances beyond this distance
    });
    
    // Apply fog to scene if enabled
    if (gpuPerf.enableFog && this.scene) {
      // Create exponential fog for more realistic distance fading
      const fogColor = new THREE.Color(0xCCDDFF);
      const fog = new THREE.FogExp2(fogColor, 0.005);
      this.scene.fog = fog;
      
      // Store fog reference for later updates
      this.optimizationSystem.fog = fog;
    }
    
    console.log('Optimization system initialized with settings:', {
      lodLevels,
      lodNear,
      lodFar,
      drawDistance,
      enableFog: gpuPerf.enableFog
    });
  }
  
  /**
   * Setup listener for quality change events from OptimizationSystem
   */
  setupQualityChangeListener() {
    if (typeof window !== 'undefined') {
      window.addEventListener('island-quality-changed', this.handleQualityChange.bind(this));
      console.log('Island quality change listener setup.');
    } else {
      console.warn('Window object not found, cannot setup quality change listener.');
    }
  }

  /**
   * Handle quality changes dispatched by the OptimizationSystem
   * @param {CustomEvent} event - The event containing new quality settings
   */
  handleQualityChange(event) {
    if (!this.scene || !this.options.enableOptimization || !this.optimizationSystem) return;

    const settings = event.detail;
    console.log('Received island quality change:', settings);

    // --- Update Scene Options (Example for Vegetation) ---
    // You might need to adjust how density changes are applied in VegetationSystem
    if (this.vegetationSystem && this.options.enableVegetation) {
        const densityChanged = 
            this.options.grassDensity !== settings.grassDensity ||
            this.options.treeDensity !== settings.treeDensity ||
            this.options.bushDensity !== settings.bushDensity;
            
        this.options.grassDensity = settings.grassDensity;
        this.options.treeDensity = settings.treeDensity;
        this.options.bushDensity = settings.bushDensity;

        if (densityChanged) {
            console.log('Applying new vegetation densities...');
            // TODO: Implement a method in VegetationSystem to update densities dynamically
            // For now, we might need to dispose and recreate vegetation if dynamic update isn't possible
            // this.vegetationSystem.updateDensities(settings); 
            console.warn('Dynamic vegetation density update not implemented. Requires manual scene reload or further implementation.');
        }
    }

    // --- Recreate Terrain LOD ---
    // Check if LOD settings actually changed
    const lodSettingsChanged = 
        this.optimizationSystem.options.lodLevels !== settings.lodLevels ||
        this.optimizationSystem.options.lodNear !== settings.lodNear ||
        this.optimizationSystem.options.lodFar !== settings.lodFar;

    if (lodSettingsChanged && this.terrainLOD) {
      console.log('Recreating terrain LOD with new settings...');
      
      // 1. Get the original full-detail mesh (assuming it's the first level of the current LOD)
      const originalMesh = this.terrainLOD.levels[0]?.object;
      if (!originalMesh) {
          console.error("Could not find original mesh in current LOD object. Cannot recreate LOD.");
          return;
      }
      
      // 2. Remove old LOD from scene, objects list, collision list, and optimization system
      this.scene.remove(this.terrainLOD);
      this.objects = this.objects.filter(obj => obj !== this.terrainLOD);
      this.collisionObjects = this.collisionObjects.filter(obj => obj !== this.terrainLOD);
      this.optimizationSystem.removeLODObject(this.terrainLOD); // Need to add this method to OptimizationSystem

      // 3. Update OptimizationSystem internal options (already done by applyQualityLevel, but good practice)
      // this.optimizationSystem.options.lodLevels = settings.lodLevels;
      // this.optimizationSystem.options.lodNear = settings.lodNear;
      // this.optimizationSystem.options.lodFar = settings.lodFar;

      // 4. Create and add new LOD
      // Note: applyTerrainLOD uses the *current* options within optimizationSystem, which should have been updated by applyQualityLevel
      this.terrainLOD = this.optimizationSystem.applyTerrainLOD(originalMesh, this.scene);
      this.objects.push(this.terrainLOD);
      this.collisionObjects.push(this.terrainLOD); // Add new LOD for collision
      
      console.log('New terrain LOD created and added.');
    } else if (lodSettingsChanged) {
        console.warn("LOD settings changed, but no existing terrain LOD object found to replace.");
    }
  }

  /**
   * Create a terrain mesh from the height map
   * @returns {THREE.Mesh} The terrain mesh
   */
  createTerrainMesh() {
    // Calculate world size (30 units is a reasonable size for gameplay)
    const worldSize = 256;
    const heightScale = 10; // Maximum height of mountains
    
    // Create a plane geometry with segment count based on performance settings
    let segmentSize;
    
    // Adjust segment size based on texture quality setting
    if (this.options.gpuPerformance && this.options.gpuPerformance.textureQuality) {
      switch (this.options.gpuPerformance.textureQuality) {
        case 'low':
          segmentSize = Math.max(4, Math.floor(this.options.size / 32));
          break;
        case 'medium':
          segmentSize = Math.min(4, Math.max(2, Math.floor(this.options.size / 64)));
          break;
        case 'high':
          segmentSize = Math.min(2, Math.max(1, Math.floor(this.options.size / 128)));
          break;
        default:
          segmentSize = Math.min(4, Math.max(1, Math.floor(this.options.size / 64)));
      }
    } else {
      segmentSize = Math.min(4, Math.max(1, Math.floor(this.options.size / 64)));
    }
    
    const segments = Math.floor(this.options.size / segmentSize);
    
    console.log(`Creating terrain with ${segments} segments (segment size: ${segmentSize})`);
    
    // Use BufferGeometry directly for better performance
    const geometry = markRaw(new THREE.PlaneGeometry(
      worldSize, 
      worldSize, 
      segments, 
      segments
    ));
    
    // Pre-allocate array for better performance during vertex updates
    const positions = geometry.attributes.position.array;
    
    // Calculate the scaling factor
    const mapScale = this.options.size / segments;
    
    // Update vertex positions based on the height map with optimized array access
    for (let i = 0, j = 0; i < positions.length; i += 3, j++) {
      // Get indices for the current vertex
      const ix = j % (segments + 1);
      const iy = Math.floor(j / (segments + 1));
      
      // Convert from geometry indices to height map indices
      const hmX = Math.floor((ix / segments) * this.options.size);
      const hmY = Math.floor((iy / segments) * this.options.size);
      
      // Clamp indices to valid range
      const heightX = Math.min(this.options.size - 1, Math.max(0, hmX));
      const heightY = Math.min(this.options.size - 1, Math.max(0, hmY));
      
      // Get height from the height map
      const height = this.heightMap[heightY][heightX] * heightScale;
      
      // Update z coordinate (height)
      positions[i + 2] = height;
    }
    
    // Flag geometry needs update
    geometry.attributes.position.needsUpdate = true;
    
    // Update normals for proper lighting
    geometry.computeVertexNormals();
    
    // Create shader-based material with texture blending with performance options
    const material = TextureManager.createTerrainMaterial(
      this.heightMap,
      this.islandMask,
      this.biomeMap,
      {
        textureQuality: this.options.gpuPerformance?.textureQuality || 'medium',
        useCompressedTextures: this.options.gpuPerformance?.useCompressedTextures || false,
        useShaderLOD: this.options.gpuPerformance?.useShaderLOD || false
      }
    );
    
    material.uniforms.uVisualizationMode = { value: 2 };
    
    // Enable shadow casting/receiving based on performance settings
    const enableShadows = this.options.gpuPerformance?.enableShadows !== undefined 
      ? this.options.gpuPerformance.enableShadows 
      : true;
      
    // Create and return the mesh
    const mesh = markRaw(new THREE.Mesh(geometry, material));
    
    // Enable shadow receiving if shadows are enabled
    mesh.receiveShadow = enableShadows;
    
    // Only create custom depth material if shadows are enabled
    if (enableShadows) {
      // Create a custom depth material to ensure proper shadow rendering
      const customDepthMaterial = new THREE.MeshDepthMaterial({
        depthPacking: THREE.RGBADepthPacking
      });
      
      // Apply the same vertex transformations to depth material
      customDepthMaterial.onBeforeCompile = (shader) => {
        // Add the same vertex displacement logic
        shader.vertexShader = shader.vertexShader.replace(
          '#include <displacementmap_vertex>',
          `#include <displacementmap_vertex>
          // Apply the same height displacement as in the main material
          transformed += normal * position.z;`
        );
      };
      
      // Assign custom depth material to the terrain mesh
      mesh.customDepthMaterial = customDepthMaterial;
    }
    
    // Mark as terrain so collision detection knows to ignore it
    mesh.userData = { isMap: true };
    
    // Rotate the mesh to be horizontal
    mesh.rotation.x = -Math.PI / 2;
    
    // Position at center
    mesh.position.set(0, 0, 0);
    
    return mesh;
  }
  
  /**
   * Create debug visualizations for the island map
   * @param {THREE.Scene} scene - The Three.js scene
   */
  createDebugVisualizations(scene) {
    // Create a preview of the heightmap as a plane
    const heightMapTexture = this.createHeightMapTexture();
    
    const debugPlaneGeometry = markRaw(new THREE.PlaneGeometry(25, 25));
    const debugPlaneMaterial = markRaw(new THREE.MeshBasicMaterial({ 
      map: heightMapTexture,
      side: THREE.DoubleSide
    }));
    
    const debugPlane = markRaw(new THREE.Mesh(debugPlaneGeometry, debugPlaneMaterial));
    debugPlane.rotation.x = -Math.PI / 2; // Horizontal
    debugPlane.position.set(0, 0.1, 0); // Just above the ground
    
    scene.add(debugPlane);
    this.debugObjects.push(debugPlane);
    
    // Create a biome map preview if biome data is available
    if (this.biomeMap) {
      const biomeMapPreview = BiomeManager.generateBiomeMapPreview(this.biomeMap);
      
      // Create a texture from the biome map preview
      const biomeTexture = markRaw(new THREE.TextureLoader().load(biomeMapPreview));
      biomeTexture.minFilter = THREE.LinearFilter;
      biomeTexture.magFilter = THREE.LinearFilter;
      
      const biomePlaneGeometry = markRaw(new THREE.PlaneGeometry(25, 25));
      const biomePlaneMaterial = markRaw(new THREE.MeshBasicMaterial({
        map: biomeTexture,
        side: THREE.DoubleSide
      }));
      
      const biomePlane = markRaw(new THREE.Mesh(biomePlaneGeometry, biomePlaneMaterial));
      biomePlane.rotation.x = -Math.PI / 2; // Horizontal
      biomePlane.position.set(30, 0.1, 0); // Next to height map, just above ground
      
      scene.add(biomePlane);
      this.debugObjects.push(biomePlane);
    }
    
    console.log('Debug visualizations created');
  }
  
  /**
   * Create a texture from the height map for visualization
   * @returns {THREE.Texture} The height map as a texture
   */
  createHeightMapTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = this.options.size;
    canvas.height = this.options.size;
    const ctx = canvas.getContext('2d');
    
    // Create an image data object
    const imageData = ctx.createImageData(this.options.size, this.options.size);
    
    // Fill the image data based on the height map
    for (let y = 0; y < this.options.size; y++) {
      for (let x = 0; x < this.options.size; x++) {
        const heightValue = this.heightMap[y][x];
        const maskValue = this.islandMask[y][x];
        const index = (y * this.options.size + x) * 4;
        
        // Color based on height and mask value
        if (maskValue === 0) {
          // Deep water
          imageData.data[index] = 0;
          imageData.data[index + 1] = 20;
          imageData.data[index + 2] = 120;
        } else if (maskValue === 0.3) {
          // Shallow water
          imageData.data[index] = 30;
          imageData.data[index + 1] = 100;
          imageData.data[index + 2] = 200;
        } else if (maskValue === 0.7) {
          // Beach
          imageData.data[index] = 240;
          imageData.data[index + 1] = 220;
          imageData.data[index + 2] = 170;
        } else {
          // Land - color based on height
          if (heightValue < 0.5) {
            // Lower land - more green
            imageData.data[index] = 50;
            imageData.data[index + 1] = 150 + heightValue * 100;
            imageData.data[index + 2] = 50;
          } else {
            // Higher land - brown to gray
            const t = (heightValue - 0.5) * 2; // 0 to 1
            imageData.data[index] = 100 + t * 100;
            imageData.data[index + 1] = 100 - t * 50;
            imageData.data[index + 2] = 50 + t * 100;
          }
        }
        
        imageData.data[index + 3] = 255; // Alpha
      }
    }
    
    // Put the image data on the canvas
    ctx.putImageData(imageData, 0, 0);
    
    // Create a texture from the canvas
    const texture = markRaw(new THREE.CanvasTexture(canvas));
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    
    return texture;
  }
  
  /**
   * Place spawn points at suitable locations on the island
   */
  placeSpawnPoints() {
    // Clear any existing spawn points
    this.spawnPoints = [];
    
    // Calculate regions to search for spawn points
    const center = Math.floor(this.options.size / 2);
    const maxRadius = Math.floor(this.options.size / 3);
    
    // Divide the island into segments for spawn point distribution
    const spawnCount = this.options.spawnPointCount;
    const angleStep = (2 * Math.PI) / spawnCount;
    
    // Find suitable spawn positions in different regions of the island
    for (let i = 0; i < spawnCount; i++) {
      // Calculate a direction from the center
      const angle = i * angleStep;
      const dirX = Math.cos(angle);
      const dirY = Math.sin(angle);
      
      // Find a region to search in (somewhere between center and edge)
      const minDistance = maxRadius * 0.3; // Not too close to center
      const maxDistance = maxRadius * 0.7; // Not too close to edge
      
      // Calculate search region based on direction
      const searchCenterX = center + Math.floor(dirX * ((minDistance + maxDistance) / 2));
      const searchCenterY = center + Math.floor(dirY * ((minDistance + maxDistance) / 2));
      const searchRadius = Math.floor((maxDistance - minDistance) / 2);
      
      // Define search region boundaries
      const minX = Math.max(0, searchCenterX - searchRadius);
      const maxX = Math.min(this.options.size - 1, searchCenterX + searchRadius);
      const minY = Math.max(0, searchCenterY - searchRadius);
      const maxY = Math.min(this.options.size - 1, searchCenterY + searchRadius);
      
      // Find a suitable spawn position in this region
      const spawnPos = this.findSpawnPosition(minX, maxX, minY, maxY);
      
      // Convert to world coordinates
      const scaleFactor = 30 / this.options.size; // Scale to match a 30x30 unit world
      const worldPos = markRaw(new THREE.Vector3(
        (spawnPos.x - center) * scaleFactor,
        1, // Height above ground
        (spawnPos.y - center) * scaleFactor
      ));
      
      // Set the Y position to the terrain height plus an offset to avoid clipping
      const terrainHeight = this.getTerrainHeightAt(worldPos.x, worldPos.z);
      worldPos.y = terrainHeight + TERRAIN_Y_OFFSET;
      
      // Add to spawn points array
      this.spawnPoints.push(worldPos);
    }
    
    console.log(`Placed ${this.spawnPoints.length} spawn points around the island`);
  }
  
  /**
   * Find a suitable spawn position in a given area
   * @param {number} minX - Minimum X coordinate
   * @param {number} maxX - Maximum X coordinate
   * @param {number} minY - Minimum Y coordinate
   * @param {number} maxY - Maximum Y coordinate
   * @returns {Object} Position with x and y coordinates
   */
  findSpawnPosition(minX, maxX, minY, maxY) {
    // Default fallback position
    let bestX = Math.floor((minX + maxX) / 2);
    let bestY = Math.floor((minY + maxY) / 2);
    let bestFlatness = 0;
    
    // Look for the flattest area in the specified region
    for (let y = minY; y < maxY; y++) {
      for (let x = minX; x < maxX; x++) {
        // Skip if not on land
        if (this.islandMask[y][x] !== 1) continue;
        
        // Calculate how flat the area is by checking surrounding points
        let flatness = this.calculateFlatness(x, y, 3); // Check in a 7x7 grid
        
        if (flatness > bestFlatness) {
          bestFlatness = flatness;
          bestX = x;
          bestY = y;
        }
      }
    }
    
    return { x: bestX, y: bestY };
  }
  
  /**
   * Calculate how flat an area is around a point
   * @param {number} centerX - Center X coordinate
   * @param {number} centerY - Center Y coordinate
   * @param {number} radius - Radius to check
   * @returns {number} Flatness value (higher is flatter)
   */
  calculateFlatness(centerX, centerY, radius) {
    const centerHeight = this.heightMap[centerY][centerX];
    let totalDifference = 0;
    let pointsChecked = 0;
    
    for (let y = Math.max(0, centerY - radius); y <= Math.min(this.options.size - 1, centerY + radius); y++) {
      for (let x = Math.max(0, centerX - radius); x <= Math.min(this.options.size - 1, centerX + radius); x++) {
        // Skip the center point
        if (x === centerX && y === centerY) continue;
        
        // Skip if not on land
        if (this.islandMask[y][x] !== 1) continue;
        
        const height = this.heightMap[y][x];
        totalDifference += Math.abs(height - centerHeight);
        pointsChecked++;
      }
    }
    
    // If not enough points were checked, consider it not flat
    if (pointsChecked < 8) return 0;
    
    // Return inverse of average difference (so higher value means flatter)
    return 1 / (1 + (totalDifference / pointsChecked));
  }
  
  /**
   * Create visual indicators for spawn points
   * @param {THREE.Scene} scene - The Three.js scene
   */
  createSpawnPointIndicators(scene) {
    // Define colors for spawn indicators (cycling through multiple colors)
    const colors = [
      0xff0000, // Red
      0x0000ff, // Blue
      0x00ff00, // Green
      0xffff00, // Yellow
      0xff00ff, // Magenta
      0x00ffff, // Cyan
      0xff8000, // Orange
      0x8000ff  // Purple
    ];
    
    // Create an indicator for each spawn point
    this.spawnPoints.forEach((spawnPos, index) => {
      // Select a color from the array (cycle through colors if more spawn points than colors)
      const colorIndex = index % colors.length;
      const color = colors[colorIndex];
      
      // Create the indicator geometry
      const spawnGeometry = markRaw(new THREE.CylinderGeometry(0.5, 0.5, 0.1, 16));
      const spawnMaterial = markRaw(new THREE.MeshStandardMaterial({ 
        color: color,
        emissive: color,
        emissiveIntensity: 0.5,
        transparent: true,
        opacity: 0.7
      }));
      
      const spawnIndicator = markRaw(new THREE.Mesh(spawnGeometry, spawnMaterial));
      spawnIndicator.position.copy(spawnPos);
      // Position the indicator just above the terrain
      spawnIndicator.position.y = spawnPos.y + TERRAIN_Y_OFFSET;
      spawnIndicator.rotation.x = Math.PI / 2;
      
      // Mark as non-collidable visual indicator only
      spawnIndicator.userData = { isVisualIndicator: true, isObstacle: false };
      
      scene.add(spawnIndicator);
      this.objects.push(spawnIndicator);
    });
  }

  /**
   * Get a random spawn position
   * @returns {THREE.Vector3} Random spawn position
   */
  getRandomSpawnPosition() {
    if (this.spawnPoints.length === 0) {
      console.warn('No spawn points available, returning origin');
      return new THREE.Vector3(0, 1, 0);
    }
    
    // Select a random spawn point
    const randomIndex = Math.floor(Math.random() * this.spawnPoints.length);
    return this.spawnPoints[randomIndex];
  }

  /**
   * Get all spawn positions
   * @returns {Array<THREE.Vector3>} Array of all spawn positions
   */
  getAllSpawnPositions() {
    return this.spawnPoints;
  }

  /**
   * Get all map objects
   * @returns {Array} Array of map objects
   */
  getMapObjects() {
    // If we have collision objects, include them with the regular objects
    if (this.collisionObjects && this.collisionObjects.length > 0) {
      // Return a new array with both regular objects and collision objects
      // Using a Set to eliminate duplicates (since landmarks are in both arrays)
      return [...new Set([...this.objects, ...this.collisionObjects])];
    }
    
    // Otherwise just return the regular objects
    return this.objects;
  }

  /**
   * Get the terrain height at a specific world position
   * @param {number} worldX - World X coordinate
   * @param {number} worldZ - World Z coordinate
   * @returns {number} Height at the specified position
   */
  getTerrainHeightAt(worldX, worldZ) {
    if (!this.heightMap) {
      console.warn('Height map not available');
      return 0;
    }
    
    // World size and height scaling from createTerrainMesh
    const worldSize = 256;
    const heightScale = 10;
    
    // Convert world coordinates to height map indices
    // worldX and worldZ are in range [-worldSize/2, worldSize/2]
    // We need to convert to height map indices [0, options.size-1]
    const normalizedX = (worldX + worldSize / 2) / worldSize; // 0 to 1
    const normalizedZ = (worldZ + worldSize / 2) / worldSize; // 0 to 1
    
    // Convert normalized coordinates to height map indices
    const hmX = Math.floor(normalizedX * this.options.size);
    const hmZ = Math.floor(normalizedZ * this.options.size);
    
    // Clamp indices to valid range
    const heightX = Math.min(this.options.size - 1, Math.max(0, hmX));
    const heightZ = Math.min(this.options.size - 1, Math.max(0, hmZ));
    
    // Get height from the height map
    const height = this.heightMap[heightZ][heightX] * heightScale;
    
    // If we're near the edge of a tile, try to smooth the height using bilinear interpolation
    // This helps avoid sudden jumps in terrain height that could lead to shadow issues
    if (heightX < this.options.size - 1 && heightZ < this.options.size - 1) {
      const fracX = normalizedX * this.options.size - hmX;
      const fracZ = normalizedZ * this.options.size - hmZ;
      
      // Sample heights at four corners of the current cell
      const h00 = this.heightMap[heightZ][heightX] * heightScale;
      const h10 = this.heightMap[heightZ][heightX + 1] * heightScale;
      const h01 = this.heightMap[heightZ + 1][heightX] * heightScale;
      const h11 = this.heightMap[heightZ + 1][heightX + 1] * heightScale;
      
      // Bilinear interpolation for smoother terrain height
      const hx0 = h00 * (1 - fracX) + h10 * fracX;
      const hx1 = h01 * (1 - fracX) + h11 * fracX;
      const smoothedHeight = hx0 * (1 - fracZ) + hx1 * fracZ;
      
      return smoothedHeight;
    }
    
    return height;
  }

  /**
   * Check if a position is in deep water (where heightmap value is < 0.2)
   * @param {number} worldX - World X coordinate
   * @param {number} worldZ - World Z coordinate
   * @returns {boolean} True if the position is in deep water
   */
  isInDeepWater(worldX, worldZ) {
    // Allow a small area around the world boundary to be treated as land
    // This prevents players from getting stuck at the edge of the map
    const worldSize = 256;
    const boundaryMargin = 2; // Units from the edge that are still walkable
    
    // Check if we're too close to the world boundary
    if (Math.abs(worldX) > (worldSize/2 - boundaryMargin) || 
        Math.abs(worldZ) > (worldSize/2 - boundaryMargin)) {
      return true; // Treat world edges as water to keep players inside the map
    }
    
    if (!this.heightMap || !this.islandMask) {
      console.warn('Height map or island mask not available');
      return false;
    }
    
    // Convert world coordinates to height map indices
    const normalizedX = (worldX + worldSize / 2) / worldSize; // 0 to 1
    const normalizedZ = (worldZ + worldSize / 2) / worldSize; // 0 to 1
    
    // Early bounds check
    if (normalizedX < 0 || normalizedX > 1 || normalizedZ < 0 || normalizedZ > 1) {
      return true; // Consider out-of-bounds as deep water to prevent moving off the map
    }
    
    // Convert normalized coordinates to height map indices
    const hmX = Math.floor(normalizedX * this.options.size);
    const hmZ = Math.floor(normalizedZ * this.options.size);
    
    // Clamp indices to valid range
    const heightX = Math.min(this.options.size - 1, Math.max(0, hmX));
    const heightZ = Math.min(this.options.size - 1, Math.max(0, hmZ));
    
    // Get height and mask values 
    const heightValue = this.heightMap[heightZ][heightX];
    const maskValue = this.islandMask[heightZ][heightX];
    
    // Use a simpler, more reliable check - primarily based on the island mask
    // maskValue of 0 is definitely deep water, and 0.3 is shallow water
    if (maskValue === 0) {
      return true;
    }
    
    // For areas near the shore, be more lenient
    if (maskValue === 0.3 && heightValue < 0.15) {
      return true;
    }
    
    return false;
  }

  /**
   * Validate a position to ensure player can move there (not in deep water)
   * @param {number} worldX - World X coordinate
   * @param {number} worldZ - World Z coordinate
   * @returns {boolean} True if the position is valid for player movement
   */
  isValidPlayerPosition(worldX, worldZ) {
    // Don't allow players to move into deep water
    return !this.isInDeepWater(worldX, worldZ);
  }

  /**
   * Create vegetation for the island
   * @param {THREE.Scene} scene - The Three.js scene to add vegetation to
   */
  createVegetation(scene) {
    console.log('Creating vegetation...');
    
    // Initialize vegetation system
    this.vegetationSystem = new VegetationSystem({
      worldSize: 256, // Match terrain size
      mapSize: this.options.size,
      seed: this.options.seed + '-vegetation',
      treeDensity: this.options.treeDensity,
      bushDensity: this.options.bushDensity,
      grassDensity: this.options.grassDensity,
      useInstancing: true, // Use instancing for better performance
      debug: true // Enable debug mode for collision visualization
    });
    
    // Place vegetation
    const vegetationObjects = this.vegetationSystem.placeVegetation(this.biomeMap, this.heightMap, scene);
    
    // Add vegetation objects to our objects array for cleanup
    this.objects.push(...vegetationObjects);
    
    console.log(`Added ${vegetationObjects.length} vegetation objects`);
    console.log('Debug mode is enabled. Press "B" key to toggle collision mesh visualization.');
  }

  /**
   * Create landmarks (points of interest) on the island
   * @param {THREE.Scene} scene - The Three.js scene
   */
  createLandmarks(scene) {
    console.log('Creating landmarks on the island...');
    
    // Initialize the Points of Interest system
    this.poiSystem = new PointsOfInterestSystem({
      seed: this.options.seed + '-landmarks',
      count: this.options.landmarkCount,
      minDistance: 30, // Minimum distance between landmarks in world units
      debug: this.options.debug
    });
    
    // Place landmarks using the points of interest system
    const landmarks = this.poiSystem.placeLandmarks(
      this.heightMap,
      this.biomeMap,
      this.islandMask,
      scene,
      this.spawnPoints // Pass spawn points to avoid placing landmarks too close to spawns
    );
    
    // Add landmarks to object collection for proper cleanup
    this.objects.push(...landmarks);
    
    // Initialize collisionObjects array if it doesn't exist
    if (!this.collisionObjects) {
      this.collisionObjects = [];
    }
    
    // Register landmarks and their meshes as collision objects
    landmarks.forEach(landmark => {
      // Log collision object registration
      console.log(`Registering landmark "${landmark.userData.name}" for collision detection`);
      
      // Add main landmark group to collision objects
      this.collisionObjects.push(landmark);
      
      // Extract all meshes from the landmark for collision detection
      landmark.traverse(object => {
        if (object.isMesh) {
          // Ensure mesh has computed bounding box for collision detection
          if (!object.geometry.boundingBox) {
            object.geometry.computeBoundingBox();
          }
          
          // Make sure the mesh has the obstacle property
          if (!object.userData.isObstacle) {
            object.userData.isObstacle = true;
          }
          
          // Add individual meshes to collision objects for more precise collision
          this.collisionObjects.push(object);
         // console.log(`  - Added mesh component for collision: ${object.uuid.substring(0, 8)}`);
        }
      });
    });
    
    console.log(`Created ${landmarks.length} landmarks on the island with ${this.collisionObjects.length - landmarks.length} mesh colliders`);
  }

  /**
   * Update the island map scene
   * @param {THREE.Camera} camera - Camera for frustum culling and LOD
   * @param {number} deltaTime - Time since last update in seconds
   */
  update(camera, deltaTime) {
    if (!this.isCreated) return;
    
    // Skip updates if frame rate is too low (performance optimization)
    if (this.lastUpdateTime > 0 && deltaTime > 0) {
      const fps = 1 / deltaTime;
      if (fps < 20) {
        // If FPS is very low, update less frequently
        // This avoids pile-up of expensive operations when the GPU is already struggling
        const now = performance.now();
        if (now - this.lastUpdateTime < 100) {
          return; // Skip this update
        }
        
        // If FPS is consistently low, auto-lower quality settings
        if (!this._lowFpsCounter) this._lowFpsCounter = 0;
        this._lowFpsCounter++;
        
        // If we detect 5 consecutive low FPS frames, try to auto-adjust quality
        if (this._lowFpsCounter >= 5 && this.options.gpuPerformance) {
          // Auto-lower quality
          const quality = this.options.gpuPerformance.textureQuality;
          if (quality === 'high') {
            console.log('Auto-lowering quality to medium due to low FPS');
            this.setGPUPerformanceLevel('medium');
          } else if (quality === 'medium') {
            console.log('Auto-lowering quality to low due to low FPS');
            this.setGPUPerformanceLevel('low');
          }
          this._lowFpsCounter = 0; // Reset counter
        }
      } else {
        // Reset the counter if FPS is okay
        if (this._lowFpsCounter) {
          this._lowFpsCounter = 0;
        }
      }
    }
    
    // Update last update time
    this.lastUpdateTime = performance.now();
    
    // Use optimization system to update LOD and frustum culling
    if (this.options.enableOptimization && this.optimizationSystem) {
      // Skip frequent updates for distant objects
      // Update frequency is proportional to distance from camera
      const cameraPos = camera.position;
      
      // Check if the camera has moved enough to warrant an update
      if (!this._lastCameraPos) {
        this._lastCameraPos = new THREE.Vector3().copy(cameraPos);
        this._timeSinceFullUpdate = 0;
      } else {
        const distanceMoved = cameraPos.distanceTo(this._lastCameraPos);
        this._timeSinceFullUpdate = (this._timeSinceFullUpdate || 0) + deltaTime;
        
        // Full update every 500ms or when camera moves significantly
        const UPDATE_THRESHOLD = 0.5; // 500ms
        const DISTANCE_THRESHOLD = 5; // 5 units
        
        if (this._timeSinceFullUpdate >= UPDATE_THRESHOLD || distanceMoved >= DISTANCE_THRESHOLD) {
          // Full update
          this._lastCameraPos.copy(cameraPos);
          this._timeSinceFullUpdate = 0;
          
          // Update optimization system with camera and objects
          this.optimizationSystem.update(deltaTime, camera, this.objects);
          
          // Update vegetation system if it exists with distance-based checks
          if (this.vegetationSystem) {
            this.vegetationSystem.update(deltaTime, camera);
          }
        } else {
          // Skip some optimization work for small updates
          // Only update nearby objects every frame
          const lodNear = this.optimizationSystem?.options?.lodNear || 30;
          const nearbyObjects = this.objects.filter(obj => {
            if (!obj.position) return false;
            return obj.position.distanceTo(cameraPos) < lodNear;
          });
          
          // Only update nearby objects
          this.optimizationSystem.updateNearObjects(deltaTime, camera, nearbyObjects);
        }
      }
    } else {
      // If optimization is disabled, still update vegetation
      if (this.vegetationSystem) {
        this.vegetationSystem.update(deltaTime, camera);
      }
    }
    
    // Measure render performance if benchmark is active
    if (this.benchmark && this.benchmark.isRenderMeasuring) {
      this.benchmark.measureFrame(deltaTime);
    }
  }

  /**
   * Start a render performance benchmark
   * @param {number} duration - Duration in frames to measure (default: 300)
   * @returns {Object} Benchmark object with control methods
   */
  startRenderBenchmark(duration = 300) {
    if (!this.benchmark) {
      this.benchmark = new PerformanceBenchmark();
    }
    
    // Start performance measurement
    this.benchmark.startRenderMeasurement(duration);
    
    return {
      isRunning: () => this.benchmark.isRenderMeasuring,
      getProgress: () => {
        if (!this.benchmark.measurements.renderPerformance.frameTimeHistory) return 0;
        return this.benchmark.measurements.renderPerformance.frameTimeHistory.length / duration;
      },
      stop: () => this.benchmark.endRenderMeasurement(),
      getStatistics: () => this.benchmark.calculateFrameTimeStatistics()
    };
  }
  
  /**
   * Get the performance benchmark results
   * @returns {Object|null} The benchmark results or null if not enabled
   */
  getBenchmarkResults() {
    if (!this.benchmark) return null;
    return this.benchmark.getMeasurements();
  }
  
  /**
   * Set a callback for performance updates during benchmarking
   * @param {Function} callback - Function to call with performance data
   */
  setPerformanceUpdateCallback(callback) {
    if (this.benchmark) {
      this.benchmark.setPerformanceUpdateCallback(callback);
    }
  }

  /**
   * Dispose of all resources
   */
  dispose() {
    console.log('Disposing island map resources');
    
    // Dispose of HDRI skybox if it exists
    if (this.hdriSkybox) {
      this.hdriSkybox.dispose();
      this.hdriSkybox = null;
    }
    
    // Clean up vegetation system if it exists
    if (this.vegetationSystem) {
      this.vegetationSystem.dispose();
      this.vegetationSystem = null;
    }
    
    // Clean up points of interest system if it exists
    if (this.poiSystem) {
      this.poiSystem.dispose();
      this.poiSystem = null;
    }
    
    // Dispose of all objects
    this.objects.forEach(object => {
      if (object.parent) {
        object.parent.remove(object);
      }
      
      // If the object has a dispose method, call it
      if (object.dispose) {
        object.dispose();
      }
      
      // If the object is a mesh, dispose of geometry and material
      if (object.geometry) {
        object.geometry.dispose();
      }
      
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach(material => {
            if (material.map) material.map.dispose();
            material.dispose();
          });
        } else {
          if (object.material.map) object.material.map.dispose();
          object.material.dispose();
        }
      }
    });
    
    // Clean up debug objects
    this.debugObjects.forEach(object => {
      if (object.parent) {
        object.parent.remove(object);
      }
      
      if (object.geometry) object.geometry.dispose();
      
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach(material => {
            if (material.map) material.map.dispose();
            material.dispose();
          });
        } else {
          if (object.material.map) object.material.map.dispose();
          object.material.dispose();
        }
      }
    });
    
    // Clear collision objects array (no need to dispose as these are references to objects in this.objects)
    this.collisionObjects = [];
    
    // Clear arrays
    this.objects = [];
    this.debugObjects = [];
    this.spawnPoints = [];
    
    // Additionally, dispose of optimization system
    if (this.optimizationSystem) {
      this.optimizationSystem.dispose();
      this.optimizationSystem = null;
    }
    
    // Remove quality change listener
    if (typeof window !== 'undefined') {
      window.removeEventListener('island-quality-changed', this.handleQualityChange.bind(this));
    }
    
    // Clear reference to scene
    this.scene = null;
    this.isCreated = false;
    
    console.log('Island map resources disposed');
  }

  /**
   * Create visual indicators for water boundaries (helpful for debugging)
   * @param {THREE.Scene} scene - The Three.js scene
   */
  createWaterBoundaryIndicators(scene) {
    if (!this.islandMask || !scene) return;
    
    console.log('Creating water boundary indicators...');
    
    // World size from createTerrainMesh
    const worldSize = 256;
    
    // Create a group to hold all boundary markers
    const boundaryGroup = new THREE.Group();
    scene.add(boundaryGroup);
    this.objects.push(boundaryGroup);
    
    // Sample the island mask at intervals
    const sampleInterval = Math.max(1, Math.floor(this.options.size / 128));
    const markers = [];
    
    for (let z = 0; z < this.options.size; z += sampleInterval) {
      for (let x = 0; x < this.options.size; x += sampleInterval) {
        const maskValue = this.islandMask[z][x];
        
        // Check if this is a boundary point (where land meets water)
        // Look for points where the mask transitions between land and water
        if (maskValue === 0.3) { // Shallow water (border area)
          // Convert heightmap coordinates to world coordinates
          const worldX = (x / this.options.size) * worldSize - worldSize / 2;
          const worldZ = (z / this.options.size) * worldSize - worldSize / 2;
          
          // Get the height at this point
          const height = this.heightMap[z][x] * 10; // Using the same height scale as elsewhere
          
          // Create a marker
          markers.push({
            position: new THREE.Vector3(worldX, height, worldZ),
            isWater: true
          });
        }
      }
    }
    
    // Create markers for the sampled boundary points
    markers.forEach(marker => {
      const geometry = new THREE.SphereGeometry(0.2, 8, 8);
      const material = new THREE.MeshBasicMaterial({ 
        color: 0x00bfff, // Deep blue for water boundaries
        transparent: true,
        opacity: 0.7
      });
      
      const boundaryMarker = new THREE.Mesh(geometry, material);
      boundaryMarker.position.copy(marker.position);
      
      // Add a small vertical offset to make sure markers are visible
      boundaryMarker.position.y += 0.1;
      
      // Mark as visual indicator only (no collision)
      boundaryMarker.userData = { 
        isVisualIndicator: true, 
        isWaterBoundary: true,
        isObstacle: false 
      };
      
      boundaryGroup.add(boundaryMarker);
    });
    
    console.log(`Created ${markers.length} water boundary indicators`);
    return boundaryGroup;
  }

  /**
   * Add an HDRI skybox to the scene
   * @param {THREE.Scene} scene - The Three.js scene
   * @param {Object} options - Optional settings for the HDRI skybox
   * @returns {Promise<HDRISkybox>} The created skybox instance
   */
  async addHDRISkybox(scene, options = {}) {
    // Import dynamically to avoid circular dependencies
    const HDRISkybox = await import('../environment/HDRISkybox.js').then(module => module.default);
    
    // Default HDRI options for the island scene
    const hdriOptions = {
      hdriPath: '/assets/environment/tropical_beach_4k.hdr', // Example path, should be adjusted to your asset
      intensity: 1.0,
      useAsBackground: true,
      useForLighting: true,
      ...options
    };
    
    // Create the HDRI skybox
    const skybox = new HDRISkybox(hdriOptions);
    
    try {
      // Create and add to scene
      await skybox.create(scene);
      console.log('HDRI skybox added to island scene');
      
      // Store reference to skybox for later cleanup
      this.hdriSkybox = skybox;
      
      return skybox;
    } catch (error) {
      console.error('Failed to add HDRI skybox:', error);
      return null;
    }
  }

  /**
   * Set GPU performance level to optimize rendering
   * @param {string} level - Performance level ('low', 'medium', 'high', 'ultra', or 'auto')
   * @returns {boolean} Whether the settings were successfully applied
   */
  setGPUPerformanceLevel(level) {
    if (!level) return false;
    
    // Auto-detect if set to auto
    if (level === 'auto') {
      // Use a simple heuristic based on window size as a basic autodetection
      // In a real app, you might want to use more sophisticated detection
      const width = window.innerWidth;
      const height = window.innerHeight;
      const pixelCount = width * height;
      
      if (pixelCount <= 1280 * 720) {
        level = 'low';
      } else if (pixelCount <= 1920 * 1080) {
        level = 'medium';
      } else if (pixelCount <= 2560 * 1440) {
        level = 'high';
      } else {
        level = 'ultra';
      }
      
      console.log(`Auto-detected GPU performance level: ${level} (based on resolution ${width}x${height})`);
    }
    
    // Define preset configurations for different performance levels
    const presets = {
      low: {
        enableShadows: false,
        shadowMapSize: 512,
        textureQuality: 'low',
        useCompressedTextures: true,
        maxInstancedObjects: 500,
        enableFog: true,
        drawDistance: 150,
        useShaderLOD: true,
        enablePostprocessing: false
      },
      medium: {
        enableShadows: true,
        shadowMapSize: 1024,
        textureQuality: 'medium',
        useCompressedTextures: true,
        maxInstancedObjects: 1000,
        enableFog: true,
        drawDistance: 250,
        useShaderLOD: true,
        enablePostprocessing: false
      },
      high: {
        enableShadows: true,
        shadowMapSize: 2048,
        textureQuality: 'high',
        useCompressedTextures: true,
        maxInstancedObjects: 2000,
        enableFog: true,
        drawDistance: 400,
        useShaderLOD: true,
        enablePostprocessing: false
      },
      ultra: {
        enableShadows: true,
        shadowMapSize: 4096,
        textureQuality: 'high',
        useCompressedTextures: false, // Use full quality textures
        maxInstancedObjects: 5000,
        enableFog: true,
        drawDistance: 600,
        useShaderLOD: false, // Always use high quality shaders
        enablePostprocessing: true
      }
    };
    
    // Get preset configuration
    const preset = presets[level];
    if (!preset) {
      console.error(`Invalid GPU performance level: ${level}`);
      return false;
    }
    
    // Update GPU performance options
    this.options.gpuPerformance = {
      ...this.options.gpuPerformance, // Keep any custom settings
      ...preset // Override with preset values
    };
    
    console.log(`Applied GPU performance level: ${level}`, this.options.gpuPerformance);
    
    // Dispatch custom event so other systems can adjust
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('island-quality-changed', {
        detail: {
          level,
          ...this.options.gpuPerformance,
          // Include additional properties that may be needed by other systems
          grassDensity: level === 'low' ? 0.3 : level === 'medium' ? 0.5 : level === 'high' ? 0.7 : 0.9,
          treeDensity: level === 'low' ? 0.1 : level === 'medium' ? 0.3 : level === 'high' ? 0.5 : 0.7,
          bushDensity: level === 'low' ? 0.2 : level === 'medium' ? 0.4 : level === 'high' ? 0.6 : 0.8,
          lodLevels: level === 'low' ? 2 : level === 'medium' ? 3 : level === 'high' ? 4 : 5,
          lodNear: level === 'low' ? 15 : level === 'medium' ? 30 : level === 'high' ? 50 : 80,
          lodFar: level === 'low' ? 100 : level === 'medium' ? 200 : level === 'high' ? 300 : 500
        }
      });
      window.dispatchEvent(event);
    }
    
    // For a complete settings update, we should reload the scene, but that might be disruptive
    // Instead we'll just update the optimization system and let other components respond to the event
    if (this.optimizationSystem) {
      const gpuPerf = this.options.gpuPerformance;
      this.optimizationSystem.options = {
        ...this.optimizationSystem.options,
        lodLevels: level === 'low' ? 2 : level === 'medium' ? 3 : level === 'high' ? 4 : 5,
        lodNear: level === 'low' ? 15 : level === 'medium' ? 30 : level === 'high' ? 50 : 80,
        lodFar: level === 'low' ? 100 : level === 'medium' ? 200 : level === 'high' ? 300 : 500,
        enableFog: gpuPerf.enableFog,
        useShaderLOD: gpuPerf.useShaderLOD,
        maxInstancedObjects: gpuPerf.maxInstancedObjects
      };
      
      // Update fog if it exists
      if (this.scene && this.scene.fog && gpuPerf.enableFog) {
        // Update fog parameters based on quality
        const fogDensity = level === 'low' ? 0.008 : level === 'medium' ? 0.005 : level === 'high' ? 0.003 : 0.001;
        this.scene.fog.density = fogDensity;
      } else if (this.scene && !gpuPerf.enableFog) {
        // Remove fog if disabled
        this.scene.fog = null;
      }
    }
    
    return true;
  }

  /**
   * Apply optimized shader complexity based on GPU performance settings
   * @param {THREE.Material} material - The material to optimize
   * @returns {THREE.Material} The optimized material
   */
  optimizeShaderComplexity(material) {
    if (!material || !this.options.gpuPerformance) return material;
    
    // Get quality setting
    const quality = this.options.gpuPerformance.textureQuality || 'medium';
    const useShaderLOD = this.options.gpuPerformance.useShaderLOD !== undefined 
      ? this.options.gpuPerformance.useShaderLOD 
      : true;
    
    // Skip optimization if shader LOD is disabled or quality is ultra
    if (!useShaderLOD || quality === 'ultra') return material;
    
    // Clone the material to avoid modifying the original
    const optimizedMaterial = material.clone();
    
    // ShaderMaterial or RawShaderMaterial optimization
    if (optimizedMaterial.isShaderMaterial) {
      // Add a quality define to both vertex and fragment shaders
      const qualityDefine = `#define QUALITY_${quality.toUpperCase()}\n`;
      
      // Add performance-based defines
      let defines = '';
      if (quality === 'low') {
        defines += '#define LOW_QUALITY\n';
        defines += '#define DISABLE_SPECULAR\n';
        defines += '#define REDUCE_TEXTURE_SAMPLES\n';
      } else if (quality === 'medium') {
        defines += '#define MEDIUM_QUALITY\n';
        defines += '#define SIMPLIFIED_LIGHTING\n';
      } else if (quality === 'high') {
        defines += '#define HIGH_QUALITY\n';
      }
      
      // Add these defines to the shaders
      if (optimizedMaterial.vertexShader) {
        optimizedMaterial.vertexShader = qualityDefine + defines + optimizedMaterial.vertexShader;
      }
      
      if (optimizedMaterial.fragmentShader) {
        optimizedMaterial.fragmentShader = qualityDefine + defines + optimizedMaterial.fragmentShader;
        
        // Replace expensive operations in fragment shader for low quality
        if (quality === 'low') {
          // Simplify specular calculations
          optimizedMaterial.fragmentShader = optimizedMaterial.fragmentShader
            .replace(/pow\(([^,]+),\s*([^\)]+)\)/g, 'pow($1, min($2, 8.0))');
          
          // Reduce normal map influence
          if (optimizedMaterial.uniforms && optimizedMaterial.uniforms.normalScale) {
            optimizedMaterial.uniforms.normalScale.value *= 0.5;
          }
        }
      }
    }
    // Regular MeshStandardMaterial or similar
    else if (optimizedMaterial.isMeshStandardMaterial) {
      // Adjust material properties based on quality
      if (quality === 'low') {
        optimizedMaterial.flatShading = true;
        optimizedMaterial.roughness = Math.min(optimizedMaterial.roughness + 0.2, 1.0);
        optimizedMaterial.metalness *= 0.8;
        optimizedMaterial.normalScale = optimizedMaterial.normalScale?.multiplyScalar(0.5) || new THREE.Vector2(0.5, 0.5);
      } else if (quality === 'medium') {
        optimizedMaterial.flatShading = false;
        // Keep default roughness and metalness
      } else {
        optimizedMaterial.flatShading = false;
        // Use default high quality settings
      }
      
      // Force these material properties for better performance
      optimizedMaterial.dithering = quality !== 'low';
      optimizedMaterial.envMapIntensity = quality === 'low' ? 0.8 : 1.0;
      
      // Adjust texture filtering
      if (optimizedMaterial.map) {
        optimizedMaterial.map.generateMipmaps = quality !== 'low';
        optimizedMaterial.map.anisotropy = quality === 'low' ? 1 : quality === 'medium' ? 4 : 16;
      }
    }
    
    return optimizedMaterial;
  }
  
  /**
   * Apply GPU performance optimizations to a mesh
   * @param {THREE.Mesh} mesh - The mesh to optimize
   * @returns {THREE.Mesh} The optimized mesh
   */
  optimizeMesh(mesh) {
    if (!mesh || !this.options.gpuPerformance) return mesh;
    
    // Get quality setting
    const quality = this.options.gpuPerformance.textureQuality || 'medium';
    
    // Optimize materials
    if (mesh.material) {
      if (Array.isArray(mesh.material)) {
        // Multiple materials
        mesh.material = mesh.material.map(mat => this.optimizeShaderComplexity(mat));
      } else {
        // Single material
        mesh.material = this.optimizeShaderComplexity(mesh.material);
      }
    }
    
    // Adjust mesh properties based on quality
    if (quality === 'low') {
      // Disable shadows for low quality
      mesh.castShadow = false;
      mesh.receiveShadow = false;
    }
    
    // Apply frustum culling
    mesh.frustumCulled = true;
    
    return mesh;
  }
}

export default IslandMapScene; 